import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environments';
import { firstValueFrom, of } from 'rxjs';
import {
  FlowNetworkFilters,
  FlowNetworkResponse,
  FlowNetworkUpdateRequest,
} from './interfaces/fe-flow-network.interfaces';
import { ErrorHandler } from '../utils/error-handler';

@Injectable({
  providedIn: 'root',
})
export class FeFlowNetworkService {
  private baseUrl = environment.WebApiURL;
  private readonly flowNetworkExportTabName = 'FlownetworkFE';

  private flowNetworkUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/GetFlowNetworkFEDetails`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }


  private multiFilterDropdownUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/GetFlowNetworkFEMultiFilterDropdown`;
  }

  private distinctTrainDetailsUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/GetFlowNetworkFEDistinctTrainDetails`;
  }

  private facilitiesByDevAreaUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/GetFlowNetworkFEFacilities`;
  }

  private updateFlowNetworkUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/UpdateFlowNetworkFE`;
  }

  constructor(private http: HttpClient) {}

  getFilterDropdownOptions(filters: FlowNetworkFilters): Promise<any> {
    const url = this.multiFilterDropdownUrl();
    return firstValueFrom(this.http.post(url, filters));
  }

  getFlowNetworkDataBackend(
    filters: FlowNetworkFilters
  ): Promise<FlowNetworkResponse> {
    const body = {
      ...filters,
      lastRowNumber: filters.lastRowNumber ?? 0,
    };

    return firstValueFrom(this.http.post<any>(this.flowNetworkUrl(), body))
      .then((response) => {
        const rows = response.feNames || [];
        const nextRowNumber = body.lastRowNumber + rows.length;
        return { rows, nextRowNumber };
      })
      .catch((error) => {
        return { rows: [], nextRowNumber: body.lastRowNumber };
      });
  }

 

  getTrainValuesByFacilityName(body: { facilityName: string }): Promise<any> {
    return firstValueFrom(this.http.post(this.distinctTrainDetailsUrl(), body));
  }

  getFacilitiesByDevArea(body: { devArea: string }): Promise<any> {
    const url = this.facilitiesByDevAreaUrl();
    return firstValueFrom(this.http.get(url, { params: body }));
  }

  async exportAllFlowNetworkData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.flowNetworkExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `fe-flow-network${inferredExtension}`;
    const rawFileName = rawHeaderFileName || fallbackFileName;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return {
      blob,
      fileName,
    };
  }

  private parseJsonResponse(response: any): any {
    let parsed = response;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { statusMessage: parsed };
      }
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      parsed = parsed[0];
    }
    return parsed;
  }

  async updateFlowNetworkFE(body: FlowNetworkUpdateRequest): Promise<any> {
    const url = this.updateFlowNetworkUrl();
    try {
      const response = await firstValueFrom(
        this.http.post(url, body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }
}
export type {
  FlowNetworkFilters,
  FlowNetworkResponse,
  FlowNetworkUpdateRequest,
} from './interfaces/fe-flow-network.interfaces';
